import React, { useState, useEffect } from 'react';
import { GCSCredentialsAPI } from '../../api/client';
import Loading from '../../components/Common/Loading';

const CredentialsPage = () => {
  const [credential, setCredential] = useState(null);
  const [accessiblePaths, setAccessiblePaths] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllPaths, setShowAllPaths] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    loadCredential();
  }, []);

  const loadCredential = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await GCSCredentialsAPI.getCredentials();
      setCredential(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load credentials');
      console.error('Error loading credentials:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const response = await GCSCredentialsAPI.download();

      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'bucket-credentials.json';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Reload to update last_used_at
      setTimeout(() => loadCredential(), 1000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to download credentials');
      console.error('Error downloading credentials:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Are you sure you want to regenerate your credentials? Your current credentials will be revoked and you will need to download the new credentials file.')) {
      return;
    }

    try {
      setRegenerating(true);
      setError(null);
      await GCSCredentialsAPI.regenerate();
      await loadCredential();
      alert('Credentials regenerated successfully! Please download the new credentials file.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to regenerate credentials');
      console.error('Error regenerating credentials:', err);
    } finally {
      setRegenerating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Loading message="Loading credentials..." size="page" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-lg font-medium text-red-800">Error</h3>
          <p className="mt-2 text-red-700">{error}</p>
          <button
            onClick={loadCredential}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!credential) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="text-lg font-medium text-yellow-800">No Credentials Found</h3>
          <p className="mt-2 text-yellow-700">
            You don't have GCS credentials yet. Credentials are automatically created when you are assigned study permissions.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">GCS Credentials</h1>
        <p className="mt-2 text-gray-600">
          Manage your Google Cloud Storage service account credentials for accessing study data.
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Credential Information Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Service Account Details</h2>
          <div className="flex gap-2">
            <button
              onClick={handleDownload}
              disabled={downloading || !credential.is_active}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {downloading ? 'Downloading...' : 'Download Credentials'}
            </button>
            <button
              onClick={handleRegenerate}
              disabled={regenerating}
              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {regenerating ? 'Regenerating...' : 'Regenerate'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500">Service Account Email</label>
            <p className="mt-1 text-sm text-gray-900 font-mono break-all">{credential.service_account_email}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Project ID</label>
            <p className="mt-1 text-sm text-gray-900">{credential.project_id}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Status</label>
            <p className="mt-1">
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                credential.is_active
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {credential.is_active ? 'Active' : 'Inactive'}
              </span>
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Created At</label>
            <p className="mt-1 text-sm text-gray-900">{formatDate(credential.created_at)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Last Regenerated</label>
            <p className="mt-1 text-sm text-gray-900">{formatDate(credential.last_regenerated_at)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Last Used</label>
            <p className="mt-1 text-sm text-gray-900">{formatDate(credential.last_used_at)}</p>
          </div>
        </div>
      </div>

      {/* Accessible Studies Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Accessible Studies</h2>
        {credential.accessible_studies && credential.accessible_studies.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {credential.accessible_studies.map((study) => (
              <span
                key={study.id}
                className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
              >
                {study.name}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No studies assigned</p>
        )}
      </div>

      {/* Usage Instructions */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-900 mb-2">How to Use</h3>
        <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
          <li>Click "Download Credentials" to download your GCS credentials JSON file</li>
          <li>Save the file as <code className="bg-blue-100 px-1 rounded">bucket-credentials.json</code> in your project directory</li>
          <li>Use the credentials file to authenticate with Google Cloud Storage in your AI scripts</li>
          <li>You can regenerate your credentials at any time if they are compromised</li>
        </ol>
      </div>
    </div>
  );
};

export default CredentialsPage;
